import React, { useState } from 'react';
import { ChevronLeft } from 'react-feather';
import { useNavigate } from 'react-router-dom';

import { useOrderDetail } from './hooks/useOrderDetail';

import DiscountHistorySection from './components/UI/DiscountHistorySection';
import EditShippingAddressModal from './components/UI/EditShippingAddressModal';
import MemoSection from './components/UI/MemoSection';
import OrderInfoSection from './components/UI/OrderInfoSection';
import OrderProductsSection from './components/UI/OrderProductsSection';
import OrderSummarySection from './components/UI/OrderSummarySection';

const OrderDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { orderData, isLoading, error, refetch } = useOrderDetail();
  const [shippingAddressModalOpen, setShippingAddressModalOpen] =
    useState(false);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (error || !orderData) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-lg text-red-500">
          Failed to load order details. Please try again.
        </div>
      </div>
    );
  }

  const hasDiscount = orderData.discountAmount > 0;

  return (
    <div className="h-full overflow-y-auto bg-gray-100 px-8 pb-12 pt-6">
      <div className="flex justify-between">
        <button
          type="button"
          className="flex cursor-pointer items-center gap-3"
          onClick={() => navigate(-1)}
        >
          <ChevronLeft size={24} className="text-[#888888]" />
          <h2 className="text-2xl font-bold leading-8">
            {orderData.orderNumber}
          </h2>
        </button>
      </div>

      <section className="mt-7 flex w-full gap-5">
        <div className="flex w-[68%] flex-col gap-5">
          <OrderSummarySection orderData={orderData} onRefetch={refetch} />

          {hasDiscount && (
            <DiscountHistorySection
              beforeDiscountAmount={orderData.beforeDiscountProductAmount}
              discountAmount={orderData.discountAmount}
              afterDiscountAmount={orderData.afterDiscountProductAmount}
              currencyCode={orderData.currencyCode}
              orderMemo={orderData.orderMemos.filter(
                (memo) => memo.type === 'PUBLIC',
              )}
            />
          )}

          <OrderProductsSection
            orderDetails={orderData.orderDetails}
            currencyCode={orderData.currencyCode}
          />
        </div>

        <div className="flex w-[32%] flex-col gap-5">
          <OrderInfoSection
            receiverInfo={orderData.receiverInfo}
            user={orderData.user}
            onEditShipping={() => setShippingAddressModalOpen(true)}
          />

          {shippingAddressModalOpen && (
            <EditShippingAddressModal
              receiverInfo={orderData.receiverInfo}
              onSuccess={refetch}
              onClose={() => setShippingAddressModalOpen(false)}
            />
          )}

          <MemoSection orderData={orderData} onRefetch={refetch} />
        </div>
      </section>
    </div>
  );
};

export default OrderDetailPage;
